import { formatISO } from "date-fns";

export const onePagerMetaList: OnePagerMeta[] = [
    {
        slug: "ai-business-app-with-rag-pipeline",
        title: "AI Business App with RAG Pipeline",
        description: "Enterprise-grade RAG platform fusing internal data with LLMs for fast, contextual decision-making.",
        excerpt: "Centralise knowledge, layer retrieval-augmented generation on top of your business data and put trusted answers in every teammate’s hands.",
        filename: "AI_Business_App_with_RAG_Pipeline.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Professional Services", "Finance & Banking", "SaaS & Technology", "Manufacturing", "Healthcare"],
        tools: ["OpenAI GPT-4", "Anthropic Claude", "Pinecone", "LangChain", "Node.js"],
        tags: ["RAG", "Enterprise Knowledge", "Decision Support", "Contextual AI"],
        costRange: { min: 20000, max: 50000, currency: "USD", label: "$20K - $50K" },
        monthlyCostRange: { min: 150, max: 1000, currency: "USD", label: "$150 - $1K / month" },
        durationWeeks: 6,
        complexity: "enterprise",
        accentClass: "from-violet-500/20 via-blue-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 4),
    },
    {
        slug: "stt-tts-multilingual-apps",
        title: "STT / TTS Multilingual Apps",
        description: "Voice-first applications with multilingual speech recognition and natural speech synthesis.",
        excerpt: "Deliver inclusive voice products that transcribe, translate and speak across markets with human-like quality.",
        filename: "STT_TTS_Multilingual_Apps.md",
        heroCta: "Calculate Personalised Voice Rollout",
        industries: ["Customer Support", "E-Learning", "Media & Entertainment", "Healthcare", "Smart Devices"],
        tools: ["Azure Speech", "Google Speech-to-Text", "Amazon Polly", "WebRTC", "Node.js"],
        tags: ["Speech AI", "Multilingual", "Accessibility", "Voice UX"],
        costRange: { min: 18000, max: 50000, currency: "USD", label: "$18K - $50K" },
        monthlyCostRange: { min: 200, max: 900, currency: "USD", label: "$200 - $900 / month" },
        durationWeeks: 5,
        complexity: "growth",
        accentClass: "from-emerald-500/20 via-cyan-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 5),
    },
    {
        slug: "ai-integrated-saas-app-with-context-reasoning",
        title: "AI-Integrated SaaS App with Reasoning over Contexts",
        description: "Context-aware SaaS platform that remembers user history and reasons about workflows to recommend next steps.",
        excerpt: "Augment your SaaS with intelligent memory, agentic reasoning and proactive guidance for every customer.",
        filename: "AI_Integrated_SaaS_App_with_Reasoning_over_Contexts.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["SaaS & Technology", "Professional Services", "Marketing Tech", "Customer Success", "Finance"],
        tools: ["OpenAI GPT-4", "Anthropic Claude", "Supabase", "PostgreSQL", "React"],
        tags: ["SaaS", "Agentic AI", "Context Store", "Recommendations"],
        costRange: { min: 22000, max: 50000, currency: "USD", label: "$22K - $50K" },
        monthlyCostRange: { min: 180, max: 900, currency: "USD", label: "$180 - $900 / month" },
        durationWeeks: 7,
        complexity: "enterprise",
        accentClass: "from-amber-500/20 via-orange-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 6),
    },
    {
        slug: "voice-calling-ai-agent",
        title: "Voice Calling AI Agent",
        description: "Real-time conversational agent automating inbound and outbound voice experiences.",
        excerpt: "Run natural voice calls that understand intent, complete workflows and escalate seamlessly to humans.",
        filename: "Voice_Calling_AI_Agent.md",
        heroCta: "Book Voice Agent Scoping Call",
        industries: ["Telecom & Contact Centres", "Finance & Banking", "Healthcare", "Retail", "Hospitality"],
        tools: ["Twilio Voice", "Deepgram", "OpenAI GPT-4", "Node.js", "Redis"],
        tags: ["Voice Agent", "Telephony", "Conversational AI", "Automation"],
        costRange: { min: 18000, max: 50000, currency: "USD", label: "$18K - $50K" },
        monthlyCostRange: { min: 200, max: 900, currency: "USD", label: "$200 - $900 / month" },
        durationWeeks: 5,
        complexity: "growth",
        accentClass: "from-sky-500/20 via-indigo-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 7),
    },
    {
        slug: "email-automation-system",
        title: "Email Automation System",
        description: "LLM-powered email engine for personalised content, segmentation and intelligent workflows.",
        excerpt: "Automate nurture journeys with AI-authored copy, behavioural triggers and analytics that keep iterating.",
        filename: "Email_Automation_System.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["SaaS & Technology", "E-Commerce", "Consulting", "Marketing Agencies", "Education"],
        tools: ["OpenAI GPT-4", "Resend", "HubSpot", "PostgreSQL", "React"],
        tags: ["Email Automation", "Personalisation", "Marketing Ops", "LLM Content"],
        costRange: { min: 15000, max: 50000, currency: "USD", label: "$15K - $50K" },
        monthlyCostRange: { min: 120, max: 800, currency: "USD", label: "$120 - $800 / month" },
        durationWeeks: 4,
        complexity: "growth",
        accentClass: "from-rose-500/20 via-fuchsia-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 8),
    },
    {
        slug: "marketing-automation-ai-agent",
        title: "Marketing Automation AI Agent",
        description: "Cross-channel AI agent orchestrating campaigns, creative variants and optimisation loops.",
        excerpt: "Coordinate campaigns across email, social and ads with an agent that tests, learns and reallocates on the fly.",
        filename: "Marketing_Automation_AI_Agent.md",
        heroCta: "Calculate Personalised Offer Cost",
        industries: ["SaaS & Technology", "E-Commerce", "Consulting", "Marketing Agencies", "Media"],
        tools: ["OpenAI GPT-4", "Zapier", "HubSpot", "Segment", "Supabase"],
        tags: ["Marketing Automation", "LLM Agent", "Growth Ops", "Cross Channel"],
        costRange: { min: 17000, max: 50000, currency: "USD", label: "$17K - $50K" },
        monthlyCostRange: { min: 130, max: 850, currency: "USD", label: "$130 - $850 / month" },
        durationWeeks: 5,
        complexity: "enterprise",
        accentClass: "from-purple-500/20 via-pink-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 9),
    },
    {
        slug: "image-generation-and-brand-asset-generator",
        title: "Image Generation & Brand Asset Generator",
        description: "Generative design studio producing on-brand visuals, templates and campaign assets at scale.",
        excerpt: "Give every team a visual production line with prompt-locked templates and instant asset variants.",
        filename: "Image_Generation_and_Brand_Asset_Generator.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["E-Commerce", "Marketing Agencies", "Consumer Products", "Media & Publishing", "Startups"],
        tools: ["Stable Diffusion", "DALL·E", "Supabase Storage", "React", "Figma API"],
        tags: ["Generative Art", "Brand System", "Creative Ops", "Asset Automation"],
        costRange: { min: 16000, max: 50000, currency: "USD", label: "$16K - $50K" },
        monthlyCostRange: { min: 150, max: 700, currency: "USD", label: "$150 - $700 / month" },
        durationWeeks: 4,
        complexity: "growth",
        accentClass: "from-orange-500/20 via-yellow-400/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 10),
    },
    {
        slug: "ai-dating-app-match-reasoning-and-compatibility",
        title: "AI Dating App (Match Reasoning + Compatibility Scoring)",
        description: "Advanced dating platform reasoning over behaviour and conversations to recommend lasting matches.",
        excerpt: "Pair people based on deep compatibility insights, personalised prompts and evolving relationship signals.",
        filename: "AI_Dating_App_Match_Reasoning_and_Compatibility_Scoring.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Consumer Apps", "Dating & Social", "Lifestyle", "Entertainment"],
        tools: ["OpenAI GPT-4", "Supabase", "React Native", "Redis", "Segment"],
        tags: ["Recommendation Engine", "Compatibility Scoring", "Conversational AI", "Mobile App"],
        costRange: { min: 20000, max: 50000, currency: "USD", label: "$20K - $50K" },
        monthlyCostRange: { min: 180, max: 900, currency: "USD", label: "$180 - $900 / month" },
        durationWeeks: 7,
        complexity: "enterprise",
        accentClass: "from-red-500/20 via-rose-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 11),
    },
    {
        slug: "custom-domain-trained-chatbots",
        title: "Custom Domain-Trained Chatbots",
        description: "Domain-accurate chat assistants trained on proprietary content with retrieval-backed answers.",
        excerpt: "Deliver precise responses anchored in your knowledge base, with guardrails, escalation and analytics.",
        filename: "Custom_Domain_Trained_Chatbots.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Legal", "Healthcare", "E-Commerce", "Finance", "Enterprise IT"],
        tools: ["OpenAI GPT-4", "LangChain", "Pinecone", "Supabase", "Next.js"],
        tags: ["Chatbot", "RAG", "Domain Expertise", "Support Automation"],
        costRange: { min: 18000, max: 50000, currency: "USD", label: "$18K - $50K" },
        monthlyCostRange: { min: 140, max: 800, currency: "USD", label: "$140 - $800 / month" },
        durationWeeks: 5,
        complexity: "growth",
        accentClass: "from-slate-500/20 via-sky-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 12),
    },
    {
        slug: "ai-document-understanding-and-contract-review",
        title: "AI Document Understanding & Contract Review",
        description: "Document intelligence engine extracting clauses, risks and insights from unstructured files.",
        excerpt: "Parse any document type, surface red flags instantly and automate downstream approval workflows.",
        filename: "AI_Document_Understanding_and_Contract_Review.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Legal", "Finance & Banking", "Manufacturing", "Government", "Professional Services"],
        tools: ["Azure Form Recognizer", "LangChain", "Supabase", "React", "OpenAI GPT-4"],
        tags: ["Document AI", "Risk Detection", "Workflow Automation", "Compliance"],
        costRange: { min: 19000, max: 50000, currency: "USD", label: "$19K - $50K" },
        monthlyCostRange: { min: 160, max: 800, currency: "USD", label: "$160 - $800 / month" },
        durationWeeks: 6,
        complexity: "enterprise",
        accentClass: "from-lime-500/20 via-emerald-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 13),
    },
    {
        slug: "ai-powered-crm-assistant",
        title: "AI-Powered CRM Assistant",
        description: "In-CRM assistant that scores leads, suggests next actions and keeps sales data razor sharp.",
        excerpt: "Give every rep a copilot that prioritises pipeline, drafts follow-ups and keeps your CRM trustworthy.",
        filename: "AI_Powered_CRM_Assistant.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["SaaS & Technology", "Consulting", "E-Commerce", "Real Estate", "Financial Services"],
        tools: ["OpenAI GPT-4", "Salesforce API", "HubSpot API", "Supabase", "React"],
        tags: ["Sales Enablement", "Lead Scoring", "CRM Automation", "Productivity"],
        costRange: { min: 18000, max: 50000, currency: "USD", label: "$18K - $50K" },
        monthlyCostRange: { min: 140, max: 800, currency: "USD", label: "$140 - $800 / month" },
        durationWeeks: 5,
        complexity: "growth",
        accentClass: "from-blue-500/20 via-cyan-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 14),
    },
    {
        slug: "video-summarisation-and-meeting-intelligence",
        title: "Video Summarisation & Meeting Intelligence Tool",
        description: "Meeting intelligence layer turning recordings into summaries, action items and searchable archives.",
        excerpt: "Never lose a decision: auto-generate recaps, assign owners and surface insights across the meeting library.",
        filename: "Video_Summarisation_and_Meeting_Intelligence_Tool.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Professional Services", "Product Teams", "Remote Organisations", "Sales", "Education"],
        tools: ["Deepgram", "OpenAI GPT-4", "Supabase", "React", "Notion API"],
        tags: ["Meeting Intelligence", "Summarisation", "Productivity", "Voice AI"],
        costRange: { min: 17000, max: 50000, currency: "USD", label: "$17K - $50K" },
        monthlyCostRange: { min: 130, max: 700, currency: "USD", label: "$130 - $700 / month" },
        durationWeeks: 5,
        complexity: "growth",
        accentClass: "from-teal-500/20 via-blue-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 15),
    },
    {
        slug: "ai-workflow-builder-no-code-orchestration-layer",
        title: "AI Workflow Builder (No-Code Orchestration Layer)",
        description: "Visual automation platform combining triggers, AI agents and SaaS integrations without code.",
        excerpt: "Let ops teams ship AI-powered workflows in minutes using reusable nodes, audit logs and governance.",
        filename: "AI_Workflow_Builder_No_Code_Orchestration_Layer.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Agencies", "Operations Teams", "E-Commerce", "Marketing", "Internal IT"],
        tools: ["n8n", "Node-RED", "OpenAI GPT-4", "Supabase", "React"],
        tags: ["No-Code", "Workflow Automation", "AI Agents", "Operations"],
        costRange: { min: 15000, max: 45000, currency: "USD", label: "$15K - $45K" },
        monthlyCostRange: { min: 100, max: 600, currency: "USD", label: "$100 - $600 / month" },
        durationWeeks: 4,
        complexity: "growth",
        accentClass: "from-yellow-500/20 via-lime-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 16),
    },
    {
        slug: "knowledgebase-auto-updater-rag-and-api-fetch",
        title: "Knowledgebase Auto-Updater (RAG + API Fetch)",
        description: "Continuous knowledge refresh pipeline ingesting docs, chat logs and APIs into an evergreen KB.",
        excerpt: "Keep answers fresh with automated ingestion, translation and retrieval-backed responses.",
        filename: "Knowledgebase_Auto_Updater_RAG_and_API_Fetch.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Software & SaaS", "Consulting", "Enterprise IT", "Compliance", "Global Teams"],
        tools: ["LangChain", "Pinecone", "Supabase", "OpenAI GPT-4", "Next.js"],
        tags: ["Knowledge Management", "RAG", "Automation", "Multilingual"],
        costRange: { min: 16000, max: 45000, currency: "USD", label: "$16K - $45K" },
        monthlyCostRange: { min: 120, max: 700, currency: "USD", label: "$120 - $700 / month" },
        durationWeeks: 4,
        complexity: "growth",
        accentClass: "from-cyan-500/20 via-blue-400/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 17),
    },
    {
        slug: "risk-fraud-and-compliance-ai-automation",
        title: "Risk, Fraud & Compliance AI Automation",
        description: "Real-time anomaly detection, compliance monitoring and risk scoring across enterprise data.",
        excerpt: "Detect fraud faster with hybrid ML + LLM pipelines, alert triage workflows and explainable insights.",
        filename: "Risk_Fraud_and_Compliance_AI_Automation.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Finance & Banking", "Insurance", "Legal", "Manufacturing", "Government"],
        tools: ["Python", "Scikit-learn", "LangChain", "Supabase", "React"],
        tags: ["Risk Analytics", "Fraud Detection", "Compliance Automation", "Anomaly Detection"],
        costRange: { min: 20000, max: 50000, currency: "USD", label: "$20K - $50K" },
        monthlyCostRange: { min: 200, max: 900, currency: "USD", label: "$200 - $900 / month" },
        durationWeeks: 6,
        complexity: "enterprise",
        accentClass: "from-indigo-500/20 via-purple-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 18),
    },
    {
        slug: "supply-chain-and-logistics-optimisation",
        title: "Supply Chain & Logistics Optimisation",
        description: "Forecasting, routing and scenario planning AI to streamline supply chain decisions.",
        excerpt: "Balance inventory, routes and demand with predictive models and actionable dashboards.",
        filename: "Supply_Chain_and_Logistics_Optimisation.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Manufacturing", "Retail & E-Commerce", "Logistics Providers", "Automotive", "Food & Beverage"],
        tools: ["Python", "Prophet", "Supabase", "React", "Google Maps API"],
        tags: ["Forecasting", "Routing", "Supply Chain", "Operations"],
        costRange: { min: 22000, max: 50000, currency: "USD", label: "$22K - $50K" },
        monthlyCostRange: { min: 180, max: 900, currency: "USD", label: "$180 - $900 / month" },
        durationWeeks: 7,
        complexity: "enterprise",
        accentClass: "from-green-500/20 via-emerald-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 19),
    },
    {
        slug: "hr-recruiting-automation",
        title: "HR / Recruiting Automation",
        description: "Talent intelligence suite automating screening, onboarding and retention signals.",
        excerpt: "Save hours on hiring pipelines with AI triage, onboarding workflows and proactive retention alerts.",
        filename: "HR_Recruiting_Automation.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Professional Services", "Healthcare", "Retail", "Technology", "Enterprise HR"],
        tools: ["OpenAI GPT-4", "Supabase", "Greenhouse API", "Workday API", "React"],
        tags: ["HR Tech", "Recruiting", "Onboarding", "Predictive People Analytics"],
        costRange: { min: 16000, max: 45000, currency: "USD", label: "$16K - $45K" },
        monthlyCostRange: { min: 120, max: 700, currency: "USD", label: "$120 - $700 / month" },
        durationWeeks: 4,
        complexity: "growth",
        accentClass: "from-fuchsia-500/20 via-purple-400/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 20),
    },
    {
        slug: "personalised-marketing-and-content-generation",
        title: "Personalised Marketing & Content Generation",
        description: "AI-led content factory producing on-brand assets and insights tailored to every audience.",
        excerpt: "Launch personalised campaigns in hours with AI copy, creative variants and closed-loop analytics.",
        filename: "Personalised_Marketing_and_Content_Generation.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["E-Commerce", "SaaS & Technology", "Media & Publishing", "Consulting", "Education"],
        tools: ["OpenAI GPT-4", "Stable Diffusion", "Supabase", "Segment", "React"],
        tags: ["Content Automation", "Personalisation", "Creative Ops", "Growth Marketing"],
        costRange: { min: 15000, max: 45000, currency: "USD", label: "$15K - $45K" },
        monthlyCostRange: { min: 110, max: 650, currency: "USD", label: "$110 - $650 / month" },
        durationWeeks: 4,
        complexity: "growth",
        accentClass: "from-orange-500/20 via-rose-400/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 21),
    },
    {
        slug: "predictive-maintenance-and-operational-monitoring",
        title: "Predictive Maintenance & Operational Monitoring",
        description: "Predictive intelligence platform that forecasts failures and optimises asset uptime.",
        excerpt: "Shift from reactive maintenance to predictive insights with sensor analytics and alerting workflows.",
        filename: "Predictive_Maintenance_and_Operational_Monitoring.md",
        heroCta: "Generate Personalised Quotation",
        industries: ["Manufacturing", "Energy & Utilities", "Automotive", "Logistics", "Infrastructure"],
        tools: ["Python", "TensorFlow", "Supabase", "Grafana", "React"],
        tags: ["Predictive Maintenance", "IoT Analytics", "Operations", "Monitoring"],
        costRange: { min: 20000, max: 50000, currency: "USD", label: "$20K - $50K" },
        monthlyCostRange: { min: 180, max: 900, currency: "USD", label: "$180 - $900 / month" },
        durationWeeks: 6,
        complexity: "enterprise",
        accentClass: "from-slate-500/20 via-emerald-500/10 to-transparent",
        publishedAt: createPublishedAt(2025, 1, 22),
    },
];

function createPublishedAt(year: number, month: number, day: number): string {
    return formatISO(new Date(Date.UTC(year, month - 1, day)));
}

export interface CostRange {
    min: number;
    max: number;
    currency: "USD";
    label: string;
}

export interface OnePagerMeta {
    slug: string;
    title: string;
    description: string;
    excerpt: string;
    filename: string;
    heroCta: string;
    industries: string[];
    tools: string[];
    tags: string[];
    costRange: CostRange;
    monthlyCostRange: CostRange;
    durationWeeks: number;
    complexity: "starter" | "growth" | "enterprise";
    accentClass: string;
    publishedAt: string;
}

// Apply a price reduction across the catalogue without mutating the source data.
// Reduces both min and max build budgets by $5k (but never below $1k).
function reduceBuildBudget(cost: CostRange): CostRange {
    const reduction = 5000;
    const min = Math.max(1000, cost.min - reduction);
    const max = Math.max(min, cost.max - reduction);
    return { ...cost, min, max, label: `$${Math.round(min / 1000)}K - $${Math.round(max / 1000)}K` };
}

export const onePagersReduced: OnePagerMeta[] = onePagerMetaList.map(item => ({
    ...item,
    costRange: reduceBuildBudget(item.costRange),
}));

